import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import Navbar from "../components/Navbar";

/**
 * A single shared layout for all three legal documents (Terms, Privacy,
 * Risk Disclosure). Page chooses which content to render based on the
 * `kind` prop. Keeps each doc auditable and easy to update without
 * touching navigation or layout chrome.
 */
const TODAY = "May 17, 2026";

const DOCS = {
  terms: {
    title: "Terms of Service",
    eyebrow: "Legal · Terms",
    updated: TODAY,
    sections: [
      {
        h: "1. Acceptance of Terms",
        p: "By creating an UpTick account or using any part of the UpTick service (\"Service\"), you agree to be bound by these Terms of Service (\"Terms\"). If you do not agree, do not use the Service.",
      },
      {
        h: "2. What UpTick Is",
        p: "UpTick is an educational charting, news-aggregation, and paper-trading tool aimed at beginner traders. UpTick does not custody funds, does not execute live trades, and does not offer brokerage, advisory, or any regulated financial service. All trading inside UpTick is simulated with virtual cash.",
      },
      {
        h: "3. Not Financial Advice",
        p: "Nothing UpTick displays — including charts, AI-generated insights, signals, news summaries, sentiment scores, watchlist suggestions, CAN SLIM scores, options Wheel scans, or any other feature — constitutes investment, tax, legal, or financial advice. You are solely responsible for any decision you make with real money outside the Service.",
      },
      {
        h: "4. Account Eligibility",
        p: "You must be at least 18 years old (or the legal age of majority in your jurisdiction) to create an account. You agree to provide accurate registration information and to keep your password confidential. You are responsible for all activity that occurs under your account.",
      },
      {
        h: "5. Acceptable Use",
        p: "You may not (a) scrape or systematically extract data from the Service, (b) use the Service to make automated trading decisions for real-money accounts, (c) reverse engineer or attempt to derive source code, (d) resell or sub-license access, (e) impersonate any person or entity, or (f) use the Service to violate any law or regulation.",
      },
      {
        h: "6. Market Data",
        p: "UpTick aggregates market data from third-party providers including Alpha Vantage, TwelveData, Yahoo Finance, Binance, and CoinGecko. Data may be delayed, incomplete, or inaccurate. UpTick makes no warranty regarding the accuracy, timeliness, or completeness of any data shown.",
      },
      {
        h: "7. AI-Generated Content",
        p: "Some explanations, summaries, and insights are produced by large language models (currently Anthropic Claude). AI output can be wrong, biased, or fabricated. Always verify against primary sources before acting on it.",
      },
      {
        h: "8. Intellectual Property",
        p: "UpTick, the UpTick mark, the green-edition branding, and all original Service content are owned by UpTick and protected by copyright and trademark law. You retain ownership of any content you post.",
      },
      {
        h: "9. Termination",
        p: "We may suspend or terminate your account at any time for any reason, including breach of these Terms. You may delete your account at any time from the Settings page.",
      },
      {
        h: "10. Disclaimer of Warranties",
        p: "The Service is provided \"as is\" without warranty of any kind. UpTick disclaims all express or implied warranties including merchantability, fitness for a particular purpose, and non-infringement.",
      },
      {
        h: "11. Limitation of Liability",
        p: "To the maximum extent permitted by law, UpTick will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, data, or use, arising out of or in connection with the Service.",
      },
      {
        h: "12. Changes to Terms",
        p: "We may update these Terms from time to time. Material changes will be notified via email or in-app banner. Continued use after a change constitutes acceptance.",
      },
      {
        h: "13. Contact",
        p: "Questions about these Terms? Email uptick_news@enlightentech.co.",
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    eyebrow: "Legal · Privacy",
    updated: TODAY,
    sections: [
      {
        h: "1. Information We Collect",
        p: "We collect (a) account info you provide: email and hashed password; (b) usage data: pages viewed, watchlists, paper trades, lesson progress, alert configurations; (c) device info: browser type, OS, IP address (for rate-limiting); (d) push-notification tokens you grant.",
      },
      {
        h: "2. How We Use It",
        p: "To operate the Service (authenticate you, sync your watchlists, deliver alerts and digest emails, compute streaks and lesson progress), to improve UpTick (anonymous funnel analytics via PostHog), and to communicate critical service notices.",
      },
      {
        h: "3. What We Don't Do",
        p: "We do not sell your personal information. We do not run advertising. We do not share your data with third parties for marketing. Trade history (paper) is private to your account.",
      },
      {
        h: "4. Third-Party Services",
        p: "Data is processed by trusted vendors needed to run the Service: SendGrid (transactional email), Anthropic (AI explanations — prompt content is sent but not retained beyond their standard policy), Firebase Cloud Messaging (push delivery), PostHog (anonymous analytics), MongoDB Atlas (encrypted storage), and our market-data providers (anonymous queries).",
      },
      {
        h: "5. Cookies & Local Storage",
        p: "We use local storage to remember your theme, dismissed banners, and onboarding state. We use a cookie/JWT pair to keep you signed in. We do not use third-party tracking cookies.",
      },
      {
        h: "6. Data Retention",
        p: "Account data is retained while your account is active. Deleting your account erases your profile, watchlists, paper trades, and lesson progress within 30 days. Aggregate, anonymized analytics may be retained for longer.",
      },
      {
        h: "7. Your Rights",
        p: "Depending on your jurisdiction, you may have the right to access, correct, or delete your personal data, or to opt out of certain processing. Email us to exercise these rights.",
      },
      {
        h: "8. Security",
        p: "Passwords are hashed with bcrypt. Connections use TLS. JWTs are signed with a server-side secret. We follow industry-standard practices, but no system is 100% secure — please use a strong, unique password.",
      },
      {
        h: "9. Children",
        p: "UpTick is not directed to children under 13 and we do not knowingly collect data from them. If you believe a child has provided us data, email us and we will delete it.",
      },
      {
        h: "10. International Transfers",
        p: "If you access UpTick from outside the country where our infrastructure is hosted, your data may be transferred internationally. By using the Service you consent to such transfers.",
      },
      {
        h: "11. Changes",
        p: "We will notify you of material changes to this Policy via email or an in-app banner. The \"Last updated\" date at the top reflects the latest revision.",
      },
      {
        h: "12. Contact",
        p: "Privacy questions or requests: uptick_news@enlightentech.co.",
      },
    ],
  },
  risk: {
    title: "Risk Disclosure",
    eyebrow: "Legal · Risk",
    updated: TODAY,
    sections: [
      {
        h: "1. Educational Tool — Not a Broker",
        p: "UpTick is an educational and research tool. It is not a broker, dealer, exchange, investment adviser, or any other regulated financial entity. UpTick never holds or transfers your money. All \"trading\" inside UpTick is paper trading with virtual cash.",
      },
      {
        h: "2. No Financial Advice",
        p: "Charts, explanations, news summaries, sentiment scores, AI \"insights\", CAN SLIM scores, Wheel-strategy scans, signals, and any other content displayed by UpTick are general information only. They are not personalized financial advice and should not be relied on for investment decisions.",
      },
      {
        h: "3. Markets Are Risky",
        p: "Trading and investing in stocks, options, and cryptocurrencies involves substantial risk, including the risk of losing more than your initial investment. Markets are volatile. Crypto is especially volatile, trades 24/7, and is unregulated in most jurisdictions. Never trade with money you cannot afford to lose.",
      },
      {
        h: "4. Past Performance ≠ Future Returns",
        p: "Any historical chart, backtest, paper-trading P&L, or AI-projected outcome shown in UpTick is descriptive of the past only. It does not predict, guarantee, or even suggest future performance.",
      },
      {
        h: "5. AI Limitations",
        p: "Generative AI explanations can be wrong, outdated, biased, or completely fabricated (\"hallucinated\"). Always verify AI claims against primary sources — official filings, exchange data, reputable news — before acting on them.",
      },
      {
        h: "6. Data Delays & Errors",
        p: "Market data comes from third-party providers and may be delayed by minutes, hours, or longer. Quotes may be stale, splits/dividends may not be reflected immediately, and crypto sources can disagree by several percent. Do not assume the price shown is the price you would receive in a real trade.",
      },
      {
        h: "7. Options & Wheel Strategy",
        p: "The Wheel options module is for learning the mechanics of cash-secured puts and covered calls. Real options trading carries unique risks including unlimited losses on uncovered positions, assignment risk, and tax complexity. Consult a licensed advisor and read the OCC's \"Characteristics and Risks of Standardized Options\" before trading real options.",
      },
      {
        h: "8. Do Your Own Research",
        p: "Always do your own research and consider speaking with a qualified, licensed financial professional before making any decision involving real money. UpTick does not endorse any specific security, strategy, or transaction.",
      },
      {
        h: "9. No Guarantee of Availability",
        p: "UpTick may experience outages, data-provider failures, or maintenance windows. We do not guarantee uninterrupted access, and we are not liable for any decision you make (or fail to make) based on Service availability.",
      },
      {
        h: "10. Acknowledgment",
        p: "By using UpTick you acknowledge that you have read and understood this Risk Disclosure, and that you assume all risks associated with any real-money trading or investing you do outside the Service.",
      },
    ],
  },
};

export default function LegalPage({ kind }) {
  const doc = DOCS[kind] || DOCS.terms;
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-[#F9F8F6] dark:bg-[#0D1117]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10 md:py-14" data-testid={`legal-page-${kind}`}>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-[#6B7068] dark:text-[#8B949E] hover:text-[#2A2F2B] dark:hover:text-[#E6EDF3] mb-6"
          data-testid="legal-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>

        <header className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#EAE6E1] dark:border-[#2A3340] bg-white dark:bg-[#161B22] text-xs mb-4">
            <FileText className="w-3.5 h-3.5 text-[#2EA44F]" strokeWidth={1.75} />
            <span className="text-[#6B7068] dark:text-[#8B949E] uppercase tracking-[0.2em] font-semibold">
              {doc.eyebrow}
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#2A2F2B] dark:text-[#E6EDF3]">
            {doc.title}
          </h1>
          <p className="text-sm text-[#6B7068] dark:text-[#8B949E] mt-3">
            Last updated: {doc.updated}
          </p>
        </header>

        <article className="space-y-8 text-[#2A2F2B] dark:text-[#E6EDF3] leading-relaxed">
          {doc.sections.map((s) => (
            <section key={s.h} data-testid={`legal-section-${kind}`}>
              <h2 className="font-heading text-xl font-semibold mb-2">{s.h}</h2>
              <p className="text-sm md:text-base text-[#2A2F2B]/85 dark:text-[#E6EDF3]/85">{s.p}</p>
            </section>
          ))}
        </article>

        <nav
          className="mt-12 flex flex-wrap gap-3 text-xs text-[#6B7068] dark:text-[#8B949E]"
          aria-label="Other legal pages"
        >
          {Object.keys(DOCS).map((k) => (
            <Link
              key={k}
              to={`/legal/${k}`}
              data-testid={`legal-cross-link-${k}`}
              className={`px-3 py-1.5 rounded-full border ${
                pathname.endsWith(k)
                  ? "border-[#2EA44F] text-[#2EA44F]"
                  : "border-[#EAE6E1] dark:border-[#2A3340] hover:border-[#2EA44F]/60"
              } transition-soft`}
            >
              {DOCS[k].title}
            </Link>
          ))}
        </nav>
      </main>
    </div>
  );
}
